<template>
    <div class="main-content">
        <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1060">
            <transition-group name="slide-fade">
                <div v-for="alert in alerts" :key="alert.id"
                    class="toast show align-items-center border-0 shadow-lg mb-2"
                    :class="'bg-' + alert.type + '-subtle text-' + alert.type" role="alert">
                    <div class="d-flex p-3">
                        <i :class="alert.icon" class="me-3 fs-5"></i>
                        <div class="toast-body p-0 fw-medium">
                            {{ alert.message }}
                        </div>
                        <button type="button" class="btn-close ms-auto" @click="removeAlert(alert.id)"></button>
                    </div>
                </div>
            </transition-group>
        </div>

        <header class="content-header mb-2 mt-n5">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb mb-0">
                    <li class="breadcrumb-item"> <router-link class="nav-link active" to="/">Início</router-link></li>
                    <li class="breadcrumb-item active text-dark small">Centros de Custo</li>
                </ol>
            </nav>
        </header>

        <div class="card p-4 shadow-subtle rounded-4 border-0">
            <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <div>
                    <h4 class="mb-0 fw-bold">Centros de Custo</h4>
                    <span class="text-muted fs-7">Exibindo {{ costCenters.length }} de {{ totalItems }}</span>
                </div>

                <div class="d-flex gap-2 align-items-center">
                    <transition name="fade">
                        <div v-if="selectedIds.length > 0" class="d-flex align-items-center me-2 pe-3 border-end">
                            <button class="btn btn-pmg-danger btn-sm px-3"
                                @click="confirmDelete(selectedIds.length, true)">
                                <i class="bi bi-trash3 me-1"></i> Excluir ({{ selectedIds.length }})
                            </button>
                        </div>
                    </transition>

                    <button class="btn btn-pmg-light" @click="showFilter = !showFilter"
                        :class="{ 'active': showFilter }">
                        <i class="bi bi-funnel"></i> Filtrar
                    </button>

                    <button class="btn btn-pmg-primary" @click="openModal('create')">
                        <i class="bi bi-plus-lg me-1"></i> Novo Centro de Custo
                    </button>
                </div>
            </div>

            <div class="collapse" :class="{ 'show': showFilter }">
                <div class="card card-body mb-4 border-0 bg-light rounded-3">
                    <div class="row g-3">
                        <div class="col-md-10">
                            <input v-model="filter.name" type="text" class="form-control form-control-pmg"
                                placeholder="Filtrar por nome ou descrição...">
                        </div>
                        <div class="col-md-2 d-flex">
                            <button class="btn btn-dark w-100 btn-pmg-dark fw-bold" @click="applyFilter">
                                <i class="bi bi-search me-1"></i> Filtrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="table-responsive">
                <table class="table table-pmg align-middle">
                    <thead>
                        <tr>
                            <th style="width: 5%">
                                <input type="checkbox" class="form-check-input" @change="toggleAll"
                                    :checked="isAllSelected">
                            </th>
                            <th style="width: 25%">Nome</th>
                            <th style="width: 55%">Descrição</th>
                            <th style="width: 15%" class="text-end">Ações</th>
                        </tr>
                    </thead>

                    <tbody v-if="costCenters.length > 0">
                        <tr v-for="item in costCenters" :key="item.id"
                            :class="{ 'selected-row': selectedIds.includes(item.id) }">
                            <td>
                                <input type="checkbox" class="form-check-input" :value="item.id" v-model="selectedIds">
                            </td>
                            <td class="fw-bold text-dark">
                                {{ item.name }}
                                <span v-if="!item.is_active" class="badge bg-secondary-subtle text-secondary ms-2 fw-medium" style="font-size: 0.65rem;">Inativo</span>
                            </td>
                            <td>
                                <div class="pmg-desc-cell text-truncate-custom">{{ item.description || 'Sem descrição' }}</div>
                            </td>
                            <td class="text-end">
                                <div class="dropdown">
                                    <button class="btn btn-pmg-icon border" data-bs-toggle="dropdown"
                                        data-bs-boundary="viewport">
                                        <i class="bi bi-three-dots-vertical"></i>
                                    </button>
                                    <ul class="dropdown-menu dropdown-menu-end shadow border-0">
                                        <li>
                                            <a class="dropdown-item" href="#" @click.prevent="openModal('edit', item)">
                                                <i class="bi bi-pencil me-2"></i>Editar
                                            </a>
                                        </li>
                                        <li><hr class="dropdown-divider"></li>
                                        <li>
                                            <a class="dropdown-item text-danger" href="#" @click.prevent="confirmDelete(item)">
                                                <i class="bi bi-trash me-2"></i>Excluir
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </td>
                        </tr>
                    </tbody>

                    <tbody v-else>
                        <tr>
                            <td colspan="4" class="text-center py-5 text-muted">
                                <div class="d-flex flex-column align-items-center justify-content-center">
                                    <i class="bi bi-inbox fs-1 mb-2 text-secondary opacity-50"></i>
                                    <span class="fw-medium">Não há registros no momento.</span>
                                    <span class="small mt-1" v-if="filter.name">Tente limpar os filtros de busca.</span>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="text-center mt-4" v-if="hasMore">
                <button class="btn btn-pmg-light px-5 btn-sm fw-bold d-inline-flex align-items-center justify-content-center"
                    @click="loadMore" :disabled="loading" style="min-width: 220px;">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    <span>{{ loading ? 'Carregando dados...' : 'Carregar mais itens' }}</span>
                </button>
            </div>
        </div>

        <div class="modal fade" id="modalCostCenter" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content border-0 shadow-lg rounded-4">
                    <div class="modal-header border-0 pt-4 px-4">
                        <h5 class="fw-bold m-0">{{ isEditing ? 'Editar Centro de Custo' : 'Novo Centro de Custo' }}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" id="closeModalBtn"></button>
                    </div>
                    <div class="modal-body px-4">
                        <div class="mb-3">
                            <label class="form-label small fw-bold text-muted">Nome</label>
                            <input v-model="form.name" type="text" class="form-control form-control-pmg"
                                placeholder="Ex: Departamento de TI, Empresa Crato, Marketing...">
                        </div>
                        <div class="mb-3">
                            <label class="form-label small fw-bold text-muted">Descrição</label>
                            <textarea v-model="form.description" class="form-control form-control-pmg" rows="3"
                                placeholder="Informe uma descrição operacional para este centro de custo"></textarea>
                        </div>
                        <div class="mb-3 d-flex align-items-center justify-content-between bg-light p-3 rounded-3" v-if="isEditing">
                            <div>
                                <label class="form-label small fw-bold text-muted d-block mb-0">Centro de Custo Ativo</label>
                                <span class="text-muted fs-7">Registros inativos não aparecem em novas faturas.</span>
                            </div>
                            <div class="form-check form-switch m-0 fs-5">
                                <input class="form-check-input" type="checkbox" role="switch" v-model="form.is_active">
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer border-0 pb-4 px-4">
                        <button class="btn btn-pmg-light me-2" data-bs-dismiss="modal">Cancelar</button>
                        <button class="btn btn-pmg-primary px-4" :disabled="!isFormValid || loading" @click="saveCostCenter">
                            {{ isEditing ? 'Atualizar' : 'Salvar Centro de Custo' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="modal fade" id="confirmDeleteModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-sm modal-dialog-centered">
                <div class="modal-content border-0 shadow-lg rounded-4 text-center p-3">
                    <div class="modal-body">
                        <div class="text-danger mb-3">
                            <i class="bi bi-exclamation-triangle-fill fs-1"></i>
                        </div>
                        <h5 class="fw-bold">Tem certeza?</h5>
                        <p class="text-muted small">
                            {{ isBulkDelete ? `Excluir ${selectedIds.length} centros de custo selecionados?` : `Excluir "${itemToDelete?.name}"?` }}
                        </p>
                    </div>
                    <div class="d-flex gap-2 justify-content-center pb-2">
                        <button class="btn btn-pmg-light px-4" data-bs-dismiss="modal">Cancelar</button>
                        <button class="btn btn-danger px-4 rounded-3" @click="executeDeletion" :disabled="loading">Confirmar</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/services/api';

export default {
    data() {
        return {
            costCenters: [],
            alerts: [],
            isEditing: false,
            form: { id: null, name: '', description: '', is_active: true },
            selectedIds: [],
            filter: { name: '' },

            // Controle de Paginação
            totalItems: 0,
            currentPage: 1,
            lastPage: 1,
            loading: false,
            hasMore: false,

            showFilter: false,
            modalInstance: null,
            itemToDelete: null,
            isBulkDelete: false
        }
    },
    computed: {
        isAllSelected() { 
            return this.costCenters.length > 0 && this.costCenters.every(i => this.selectedIds.includes(i.id)); 
        },
        isFormValid() { 
            return this.form.name && this.form.name.trim().length > 0; 
        }
    },
    mounted() {
        // Inicializa o modal nativo via Bootstrap
        const modalElement = document.getElementById('modalCostCenter');
        if (modalElement) {
            this.modalInstance = new window.bootstrap.Modal(modalElement);
        }

        // Carga inicial dos dados
        this.fetchCostCenters();
    },
    methods: {
        async fetchCostCenters(page = 1, append = false) {
            this.loading = true;
            try {
                const params = {
                    page: page,
                    search: this.filter.name
                };
                const response = await api.get('/cost-centers', { params });
                const payload = response.data;
                
                const items = Array.isArray(payload) ? payload : (payload.data || []);
                const paginationData = payload.meta || payload;
                const { current_page, last_page, total } = paginationData;

                if (append) {
                    this.costCenters = [...this.costCenters, ...items];
                } else {
                    this.costCenters = items;
                }

                this.currentPage = current_page || 1;
                this.lastPage = last_page || 1;
                this.totalItems = total || 0;
                this.hasMore = this.currentPage < this.lastPage;

            } catch (error) {
                this.showAlert('Falha ao comunicar com o servidor para buscar os registros.', 'error');
                console.error("Erro na API:", error);
            } finally {
                this.loading = false;
            }
        },

        loadMore() {
            if (this.hasMore && !this.loading) {
                this.fetchCostCenters(this.currentPage + 1, true);
            }
        },

        applyFilter() {
            this.showFilter = false;
            this.selectedIds = [];
            this.fetchCostCenters(1, false);
        },

        showAlert(message, type = 'success') {
            const id = Date.now();
            let icon = 'bi-check-circle-fill';

            if (type === 'danger') icon = 'bi-exclamation-circle-fill';
            if (type === 'warning') icon = 'bi-exclamation-triangle-fill';
            if (type === 'error') icon = 'bi-x-circle-fill';

            this.alerts.push({ id, message, type, icon });
            const timeout = type === 'error' ? 6000 : 4000;
            setTimeout(() => this.removeAlert(id), timeout);
        },

        removeAlert(id) {
            this.alerts = this.alerts.filter(a => a.id !== id);
        },

        confirmDelete(target, isBulk = false) {
            this.itemToDelete = target;
            this.isBulkDelete = isBulk;
            const modal = new window.bootstrap.Modal(document.getElementById('confirmDeleteModal'));
            modal.show();
        },

        async saveCostCenter() {
            this.loading = true;
            try {
                if (this.isEditing) {
                    const response = await api.put(`/cost-centers/${this.form.id}`, this.form);
                    const updated = response.data.data || response.data;
                    const index = this.costCenters.findIndex(c => c.id === this.form.id);
                    if (index !== -1) {
                        this.costCenters[index] = updated;
                        this.showAlert("Centro de Custo atualizado com sucesso!");
                    }
                } else {
                    const response = await api.post('/cost-centers', this.form);
                    const newRecord = response.data.data || response.data;
                    this.costCenters.unshift(newRecord);
                    this.totalItems += 1;
                    this.showAlert("Centro de Custo salvo com sucesso!");
                }

                if (this.modalInstance) {
                    this.modalInstance.hide();
                }
            } catch (e) {
                if (e.response?.status === 422) {
                    const validationErrors = e.response.data?.errors || e.response.data?.data;
                    if (!validationErrors) {
                        this.showAlert("Erro de validação. Verifique os campos.", "error");
                        return;
                    }
                    let errorMessages = "Verifique os seguintes campos:\n\n";
                    for (const field in validationErrors) {
                        errorMessages += `- ${validationErrors[field][0]}\n`;
                    }
                    this.showAlert(errorMessages, "error");
                } else {
                    const msg = e.response?.data?.message || "Erro ao processar requisição.";
                    this.showAlert(msg, "error");
                }
            } finally {
                this.loading = false;
            }
        },

        async executeDeletion() {
            this.loading = true;
            try {
                if (this.isBulkDelete) {
                    await api.post('/cost-centers/bulk-delete', { ids: this.selectedIds });
                    this.costCenters = this.costCenters.filter(c => !this.selectedIds.includes(c.id));
                    this.totalItems -= this.selectedIds.length;
                    this.showAlert(`${this.selectedIds.length} registros excluídos com sucesso.`);
                    this.selectedIds = [];
                } else {
                    await api.delete(`/cost-centers/${this.itemToDelete.id}`);
                    this.costCenters = this.costCenters.filter(c => c.id !== this.itemToDelete.id);
                    this.totalItems -= 1;
                    this.showAlert(`Centro de custo "${this.itemToDelete.name}" removido.`);
                }
            } catch (error) {
                console.error("Erro ao excluir registro(s):", error);
                const errorMessage = error.response?.data?.message || 'Não foi possível excluir o(s) registro(s).';
                this.showAlert(errorMessage, 'error');
            } finally {
                this.loading = false;
                const modalElement = document.getElementById('confirmDeleteModal');
                if (modalElement) {
                    const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                    if (modalInstance) modalInstance.hide();
                }
            }
        },

        toggleAll(e) {
            if (e.target.checked) this.selectedIds = this.costCenters.map(c => c.id);
            else this.selectedIds = [];
        },

        openModal(mode, item = null) {
            if (mode === 'edit') {
                this.isEditing = true;
                this.form = { ...item };
            } else {
                this.isEditing = false;
                this.form = { id: null, name: '', description: '', is_active: true };
            }

            if (this.modalInstance) {
                this.modalInstance.show();
            }
        }
    }
}
</script>

<style scoped>
/* ESTILOS DE ALERTA (TOASTS) */
.toast {
    min-width: 300px;
    border-radius: 12px;
    background-color: white !important;
}

.bg-success-subtle {
    background-color: #ecfdf5 !important;
    color: #059669 !important;
    border-left: 5px solid #059669 !important;
}

.bg-danger-subtle {
    background-color: #fef2f2 !important;
    color: #dc2626 !important;
    border-left: 5px solid #dc2626 !important;
}

/* LAYOUT PMG MANTIDO */
.main-content {
    background-color: #f8fafc;
    min-height: 100vh;
    padding: 2rem;
    font-family: 'Inter', sans-serif;
}

.card {
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02);
}

.btn-pmg-primary {
    background: #561569;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
    font-weight: 500;
}

.btn-pmg-primary:disabled {
    opacity: 0.5;
}

.btn-pmg-light {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    color: #64748b;
    padding: 10px 20px;
}

.btn-pmg-danger {
    background: #fee2e2;
    color: #b91c1c;
    border: 1px solid #fecaca;
    border-radius: 10px;
    font-weight: 600;
}

.form-control-pmg {
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    padding: 12px;
}

.form-control-pmg:focus {
    border-color: #561569;
    box-shadow: 0 0 0 4px rgba(33, 51, 97, 0.1);
}

/* Tabela */
.table-pmg thead th {
    background: #f8fafc;
    color: #64748b;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 1.2rem;
    border-bottom: 1px solid #edf2f7;
}

.table-pmg tbody td {
    padding: 1.2rem;
    border-bottom: 1px solid #f1f5f9;
}

.pmg-desc-cell {
    color: #64748b;
    font-size: 0.9rem;
    max-width: 500px;
}

/* Transições */
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.4s ease;
}

.slide-fade-enter-from {
    transform: translateX(100px);
    opacity: 0;
}

.slide-fade-leave-to {
    opacity: 0;
    transform: scale(0.9);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.bg-error-subtle {
    background-color: #fef2f2 !important;
    color: #991b1b !important;
    border-left: 5px solid #ef4444 !important;
    animation: shake 0.4s ease-in-out;
}

.bg-warning-subtle {
    background-color: #fffbeb !important;
    color: #92400e !important;
    border-left: 5px solid #f59e0b !important;
}

@keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    75% { transform: translateX(5px); }
}

.dropdown-item {
    font-size: 0.9rem;
    color: #334155;
    transition: all 0.2s ease;
}

.dropdown-item:hover {
    background-color: #f8fafc;
    color: #1e293b;
}

.dropdown-item.text-danger:hover {
    background-color: #fff1f2 !important;
    color: #dc2626 !important;
}

.btn-pmg-icon.border {
    border-color: #e2e8f0 !important;
    background: white;
    width: 38px;
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
}

.btn-pmg-icon:hover {
    background-color: #f1f5f9;
}
</style>